/**
 * Canonical currency / BigFloat display helpers.
 *
 * Supabase pg_graphql exposes DECIMAL/NUMERIC columns as the `BigFloat` scalar,
 * whose GraphQL OUTPUT is a **string** (e.g. `"27.5"`), never a JS number. Code
 * that does `value.toLocaleString()` or `sum + value` on a BigFloat output is a
 * latent bug: it either throws (no `.toLocaleString` on the runtime string in
 * some shapes) or silently concatenates (`"3" + 2 === "32"`).
 *
 * Always run BigFloat values through these helpers before display or math.
 * `parseBigFloat` normalizes `string | number | null | undefined` to a finite
 * number or `null`; `formatCurrency` / `formatNumber` render for the UI.
 *
 * This is the single shared implementation. Do NOT hand-roll another
 * `formatCurrency` — import from `@wtc/ui`.
 */

/** A value that may arrive as a BigFloat string, a number, or be absent. */
export type BigFloatValue = string | number | null | undefined;

/**
 * Coerce a BigFloat string / number into a finite JS number.
 * Returns `null` for `null` / `undefined` / `""` / non-numeric input — callers
 * decide the fallback rather than getting a silent `NaN`.
 */
export function parseBigFloat(value: BigFloatValue): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export interface FormatCurrencyOptions {
  /** ISO 4217 currency code. Default `"USD"`. */
  currency?: string;
  /** BCP-47 locale. Default `"en-US"`. */
  locale?: string;
  /** Returned when the value cannot be parsed. Default `null`. */
  fallback?: string | null;
  /** Minimum fraction digits. Default `0`. */
  minimumFractionDigits?: number;
  /** Maximum fraction digits. Default `2`. */
  maximumFractionDigits?: number;
}

/**
 * Format a BigFloat string / number as a localized currency string.
 * Returns `options.fallback` (default `null`) when the value is unusable.
 *
 * Overloaded on `fallback`, matching {@link formatWholeCurrency}: pass a string
 * and the return narrows to `string`, so server surfaces that must hand a Slack
 * block or PDF cell a real string do not have to coalesce at every call site.
 */
export function formatCurrency(
  value: BigFloatValue,
  options: FormatCurrencyOptions & { fallback: string }
): string;
export function formatCurrency(
  value: BigFloatValue,
  options?: FormatCurrencyOptions
): string | null;
export function formatCurrency(
  value: BigFloatValue,
  options: FormatCurrencyOptions = {}
): string | null {
  const numeric = parseBigFloat(value);
  if (numeric === null) return options.fallback ?? null;

  return new Intl.NumberFormat(options.locale ?? "en-US", {
    style: "currency",
    currency: options.currency ?? "USD",
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(numeric);
}

/**
 * Whole-dollar currency display — no fraction digits.
 *
 * This exact option pair (`minimumFractionDigits: 0, maximumFractionDigits: 0`)
 * was hand-rolled in eight dashboard widgets and quick cards, so it is a preset
 * rather than something each caller re-specifies. Use it for headline figures
 * (pipeline value, monthly charges, overdue balance) where cents are noise.
 *
 * Overloaded on `fallback`: pass a string and the return type narrows to
 * `string`, so callers feeding a prop that requires one (a stat tile's `value`)
 * do not have to widen the prop or assert. Omit it and you get `string | null`,
 * which renders as nothing in JSX.
 */
export function formatWholeCurrency(
  value: BigFloatValue,
  currency: string | undefined,
  fallback: string
): string;
export function formatWholeCurrency(
  value: BigFloatValue,
  currency?: string,
  fallback?: null
): string | null;
export function formatWholeCurrency(
  value: BigFloatValue,
  currency = "USD",
  fallback: string | null = null
): string | null {
  return formatCurrency(value, {
    currency,
    fallback,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

export interface FormatNumberOptions {
  locale?: string;
  fallback?: string | null;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Format a BigFloat string / number as a plain localized number (no currency
 * symbol) — e.g. hours, quantities. Returns `options.fallback` (default `null`)
 * when unusable.
 */
export function formatNumber(
  value: BigFloatValue,
  options: FormatNumberOptions = {}
): string | null {
  const numeric = parseBigFloat(value);
  if (numeric === null) return options.fallback ?? null;

  return new Intl.NumberFormat(options.locale ?? "en-US", {
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(numeric);
}
